import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { BaseView, BaseText, BaseSafeFormView, AppButton } from '@/components'
import { currencyUtils } from '@/utils'
import useOffsets from '@/hooks/useOffsets'
import { useAppAnalytics } from '@/services/analytics'

const { formatDynamicCurrency } = currencyUtils

// Function to get currency GIF animation
const getCoinGif = (code: string): string => {
  const coinGifs: Record<string, string> = {
    BTC: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/bitcoin-optimised.gif',
    ETH: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/eth-optimised.gif',
    XAUT: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/gold-optimised.gif',
    USDT: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/usd-optimized.gif',
    KWDK: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/kwd-optimised.gif',
    SOL: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/sol-optimized.gif',
    AEDK: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/default-optimised.gif',
    SARK: 'https://storage.googleapis.com/kem-stage/animations%20optimised%202/default-optimised.gif',
  }

  return (
    coinGifs[code] ||
    'https://storage.googleapis.com/kem-stage/animations%20optimised%202/default-optimised.gif'
  )
}

export const WithdrawSuccessScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()

  // Get params from state
  const { amount, code, title, subtitle, description, relativeFiatFormatted } =
    location.state || {}

  const { trackEvent, events } = useAppAnalytics()

  React.useEffect(() => {
    trackEvent(events.CRYPTO_ACTION_COMPLETED, {
      action_name: 'withdraw',
      token_symbol: code || 'unknown',
      amount: amount ? Number(amount) : 0,
    })
  }, [trackEvent, events, code, amount])

  const handleDone = () => {
    navigate('/activity')
  }

  // Format amount as in RN version
  const formattedAmount =
    amount && code
      ? formatDynamicCurrency({
          amount: Number(amount),
          currencyCode: code,
          showDecimals: true,
        })
      : null

  return (
    <BaseSafeFormView
      style={{
        paddingTop: offsets.top - 30,
        paddingLeft: 13,
        paddingRight: 13,
      }}
    >
      <BaseView flex={1}>
        <BaseView
          align="center"
          style={{ marginTop: 'auto', marginBottom: 'auto' }}
          gap={48}
        >
          {/* Animated currency icon */}
          {code && (
            <img
              src={getCoinGif(code)}
              alt={`${code} animation`}
              style={{
                width: 180,
                height: 180,
                objectFit: 'contain',
              }}
            />
          )}

          <BaseView gap={24}>
            {/* Title */}
            <BaseText
              fontFamily="medium"
              fontSize={24}
              textAlign="center"
              color="label.default"
            >
              {title || t('screens:withdraw.withdrawalSuccessful')}
            </BaseText>

            {/* Subtitle */}
            {subtitle && (
              <BaseText
                fontSize={20}
                color="label.description"
                textAlign="center"
              >
                {subtitle}
              </BaseText>
            )}

            {/* Amount */}
            <BaseView center gap={16}>
              {formattedAmount && (
                <BaseText
                  fontSize={32}
                  fontFamily="semibold"
                  color="label.default"
                  textAlign="center"
                >
                  {formattedAmount}
                </BaseText>
              )}

              {relativeFiatFormatted && (
                <BaseText color="label.description" textAlign="center">
                  ≈{relativeFiatFormatted}
                </BaseText>
              )}
            </BaseView>

            {/* Description */}
            {description && (
              <BaseText
                fontSize={20}
                color="label.description"
                textAlign="center"
              >
                {description}
              </BaseText>
            )}
          </BaseView>
        </BaseView>

        {/* Done button */}
        <div style={{ paddingBottom: offsets.bottom }}>
          <AppButton
            type="secondaryGray"
            label={t('form:actions.done')}
            onPress={handleDone}
            style={{ marginTop: 'auto', marginBottom: 12 }}
          />
        </div>
      </BaseView>
    </BaseSafeFormView>
  )
}
